import * as React from 'react';
import { ReactComponent as Diamond } from 'assets/icons/diamond.svg';
import { User } from 'types/User';
import { matchRoutes, useLocation } from 'react-router-dom';
import { Region } from 'services/clients/regions';

import { brandColors } from 'brandColors';
import { PresentationChartBarIcon } from '@heroicons/react/24/solid';
import { Permission } from 'utils/permissions';
import { Segment } from 'utils/permissions/segmentation';
import { PortCollectionFeature } from 'types/Port';
import * as Icons from '../assets/multi-port-icons';

interface PageDetails {
  /** A user facing description of the feature */
  description: (region: Region) => string;
  /** The icon to use in menus and lists */
  icon: JSX.Element | React.ComponentType;
  /** The color of the feature used for the icon as a valid HTML color */
  color: string;
  /** The color of any hover events as a tailwind class color */
  hoverColor: string;
  /** If set, only regions with this feature can access this page */
  regionFeature?: PortCollectionFeature;
}

export interface Page {
  title: string;
  path: string;
  isPublic?: boolean;
  isTabShown?: (user: User) => boolean;
  PageIcon?: JSX.Element;
  /**
   * The permissions required to access this page;
   * Passing a list of permissions will AND the permissions;
   * Passing an object gives more flexibility to OR or AND permissions
   * Providing both an OR and AND will AND the OR and AND
   */
  requiredPermissions?: Permission[] | { or?: Permission[]; and?: Permission[] };
  /** The segments that the page is primarily intended for */
  segmentation?: Segment[];
  /** Indicates if a page is always accessible regardless of subscription status */
  isAlwaysAccessible?: boolean;
  isSuperUser?: boolean;
  isCompanyAdmin?: boolean;
  children?: Array<Page>;
  details?: Partial<PageDetails>;
}

export enum Routes {
  SignIn = '/signin',
  SignUp = '/signup',
  Waitlist = '/waitlist',
  ResetPassword = '/reset-password',
  ChangePassword = '/change-password',
  FreeTable = '/free-table',
  EmailConfirmation = '/confirmation',
  Welcome = '/welcome',
  TermsOfUse = '/terms-of-use',
  PrivacyPolicy = '/privacy-policy',
  ContactUs = '/contact-us',
  ForceLogin = '/force-login',
  Home = '/',
  Landing = '/landing',
  VesselSchedule = '/vessel-schedule',

  // OLD, REDIRECTING ROUTES
  EmptyAppointments = '/empty-appointments',
  PerDiemFighter = '/perdiem-archive',
  TerminalScreenshots = '/perdiem-archive/terminal-screenshots',
  GateSchedule = '/gate-schedule',
  NotificationSettings = '/settings/notification-settings',
  Credentials = '/settings/credentials',
  EmptyReturnLocations = '/terminals',

  RegionalEmptyAppointments = '/:region/empty-appointments',
  RegionalPerDiemArchive = '/:region/per-diem-archive',
  RegionalTerminalScreenshots = '/:region/per-diem-archive/terminal-screenshots',
  RegionalGateSchedule = '/:region/gate-schedule',
  RegionalNotificationSettings = '/settings/:region/notification-settings',
  RegionalCredentials = '/settings/:region/credentials',
  RegionalEmptyReturnLocations = '/:region/terminals',

  Profile = '/settings/profile',
  AdminGateSchedule = '/admin/gate-schedule',
  AdminPtscSchedule = '/admin/terminal-instructions/ptsc-schedule',
  ContainerTracking = '/containers',
  ContainerDetail = '/containers/:containerNumber',
  Plans = '/settings/plans-and-pricing',
  InvoiceHistory = '/settings/billing-and-invoices',
  Settings = '/settings/',
  JoinCompany = '/join-company',
  AwaitingAdmin = '/awaiting-admin',
  CreateCompany = '/create-company',
  JoinRequestValidation = '/company/request/validate',
  ComponentPreview = '/component-preview',

  Shipments = '/shipments',
  ShipmentsDashboard = '/shipments/dashboard',
  ShipmentsAdd = '/shipments/add',
  ShipmentsActive = '/shipments/active',
  ShipmentsDeleted = '/shipments/deleted',
  ShipmentsReports = '/shipments/reports',
}

const pageInformation: Record<string, Page> = {
  [Routes.SignIn]: {
    title: 'Sign in',
    path: Routes.SignIn,
    isPublic: true,
  },
  [Routes.SignUp]: {
    title: 'Sign up',
    path: Routes.SignUp,
    isPublic: true,
  },
  [Routes.Waitlist]: {
    title: 'Waitlist',
    path: Routes.Waitlist,
    isPublic: true,
  },
  [Routes.ResetPassword]: {
    title: 'Reset password',
    path: Routes.ResetPassword,
    isPublic: true,
  },
  [Routes.ChangePassword]: {
    title: 'Reset password',
    path: Routes.ChangePassword,
    isPublic: true,
  },
  [Routes.FreeTable]: {
    title: 'Free Table',
    path: Routes.FreeTable,
    isPublic: true,
  },
  [Routes.EmailConfirmation]: {
    title: 'Email Confirmation',
    path: Routes.EmailConfirmation,
    isPublic: true,
  },
  [Routes.Welcome]: {
    title: 'Welcome',
    path: Routes.Welcome,
    isPublic: true,
  },
  [Routes.TermsOfUse]: {
    title: 'Terms of Use',
    path: Routes.TermsOfUse,
    isPublic: true,
  },
  [Routes.PrivacyPolicy]: {
    title: 'Privacy Policy',
    path: Routes.PrivacyPolicy,
    isPublic: true,
  },
  [Routes.ContactUs]: {
    title: 'Contact Us',
    path: Routes.ContactUs,
    isPublic: true,
  },
  [Routes.ForceLogin]: {
    title: 'Force Login',
    path: Routes.ForceLogin,
  },
  [Routes.Home]: {
    title: 'Home',
    path: Routes.Home,
  },
  [Routes.RegionalEmptyReturnLocations]: {
    title: 'Empty Receiving Schedule',
    path: Routes.RegionalEmptyReturnLocations,
    details: {
      description: () => 'Find which terminals are accepting empties.',
      icon: Icons.EmptyBoxSvg,
      color: brandColors.colors.blue.DEFAULT,
      hoverColor: 'blue-200',
    },
    requiredPermissions: { or: [Permission.EmptyReceivingSchedule, Permission.FreeTable] },
    segmentation: [Segment.Trucker],
  },
  [Routes.RegionalGateSchedule]: {
    title: 'Gate Schedule',
    path: Routes.RegionalGateSchedule,
    details: {
      description: (region) => `Review the gate schedules in ${region.name}.`,
      icon: Icons.DateRangeSvg,
      color: brandColors.colors.blue.DEFAULT,
      hoverColor: 'blue-200',
    },
    requiredPermissions: [Permission.GateSchedule],
    segmentation: [Segment.Trucker],
  },
  [Routes.VesselSchedule]: {
    title: 'Vessel Schedule',
    path: Routes.VesselSchedule,
    details: {
      description: (region) => `Review the vessel schedules in ${region.name}.`,
      icon: Icons.ContainerBoatSvg,
      color: brandColors.colors.blue.DEFAULT,
      hoverColor: 'blue-200',
      regionFeature: PortCollectionFeature.VesselSchedule,
    },
    requiredPermissions: [Permission.VesselSchedule],
    segmentation: [Segment.Trucker],
  },
  [Routes.RegionalEmptyAppointments]: {
    title: 'Find Empty Return Location',
    path: Routes.RegionalEmptyAppointments,

    details: {
      description: (region) => `Review empty return appointments in ${region.name}.`,
      icon: Icons.DoubleArrowsSvg,
      color: brandColors.colors.blue.DEFAULT,
      hoverColor: 'blue-200',
    },
    requiredPermissions: [Permission.EmptyReturnLocationView],
    segmentation: [Segment.Trucker],
  },
  [Routes.RegionalPerDiemArchive]: {
    title: `Per Diem Archive`,
    path: Routes.RegionalPerDiemArchive,
    PageIcon: Diamond,

    details: {
      description: (region) =>
        `Build your report with past empty return instructions from all ${region.name} terminals at once.`,
      icon: Icons.PaperFillSvg,
      color: brandColors.colors.orange.DEFAULT,
      hoverColor: 'orange-50',
    },
    requiredPermissions: { or: [Permission.PerDiemArchive, Permission.PerDiemSmartPerDiemView] },
    segmentation: [Segment.Trucker],
  },
  [Routes.RegionalTerminalScreenshots]: {
    title: `Terminal Screenshots`,
    path: Routes.RegionalTerminalScreenshots,

    details: {
      description: (region) =>
        `Retrieve screenshots of past empty return instructions terminal by terminal at the ports of ${region.name}.`,
      icon: Icons.FrameSvg,
      color: brandColors.colors.orange.DEFAULT,
      hoverColor: 'orange-50',
    },
    requiredPermissions: [Permission.TerminalScreenshots],
    segmentation: [Segment.Trucker],
  },
  [Routes.Profile]: {
    title: 'Profile & Users',
    path: Routes.Profile,
    isAlwaysAccessible: true,
  },
  [Routes.RegionalCredentials]: {
    title: 'Terminal Credentials',
    path: Routes.RegionalCredentials,
    requiredPermissions: [Permission.TerminalCredentials],
    segmentation: [Segment.Trucker],
    isAlwaysAccessible: true,
  },
  [Routes.RegionalNotificationSettings]: {
    title: 'Notification Settings',
    path: Routes.RegionalNotificationSettings,
    isAlwaysAccessible: true,
  },
  [Routes.AdminGateSchedule]: {
    title: 'Admin Gate Schedule',
    path: Routes.AdminGateSchedule,
    isSuperUser: true,
  },
  [Routes.AdminPtscSchedule]: {
    title: 'Admin PTSC Schedule',
    path: Routes.AdminPtscSchedule,
    isSuperUser: true,
  },
  [Routes.ContainerTracking]: {
    title: 'Track Containers',
    path: Routes.ContainerTracking,
    children: [
      {
        title: 'Container Detail',
        path: Routes.ContainerDetail,
      },
    ],
    details: {
      description: () => 'Track your container status at the ports.',
      icon: Icons.TableSvg,
      color: brandColors.colors.vessel.DEFAULT,
      hoverColor: 'vessel-600',
    },
    requiredPermissions: [Permission.TrackNTrace],
    segmentation: [Segment.Trucker],
  },
  [Routes.Shipments]: {
    title: 'Watchlist',
    path: Routes.Shipments,
    children: [
      {
        title: 'D&D Dashboard',
        path: Routes.ShipmentsDashboard,
        details: {
          description: () =>
            'Review your demurrage & per diem charges, and know which containers need attention.',
          icon: PresentationChartBarIcon,
          color: brandColors.colors.vessel.DEFAULT,
          hoverColor: 'vessel-600',
        },
        requiredPermissions: [Permission.ShipmentWatchListDashboard],
      },
      {
        title: 'Add shipments',
        path: Routes.ShipmentsAdd,
        requiredPermissions: [Permission.ShipmentWatchList],
      },
      {
        title: 'All active shipments',
        path: Routes.ShipmentsActive,
        requiredPermissions: [Permission.ShipmentWatchList],
        details: {
          description: () => 'Track your shipments at the ports.',
          icon: Icons.TableSvg,
          color: brandColors.colors.vessel.DEFAULT,
          hoverColor: 'vessel-600',
        },
      },
      {
        title: 'Deleted',
        path: Routes.ShipmentsDeleted,
        requiredPermissions: [Permission.ShipmentWatchList],
      },
      {
        title: 'Reports',
        path: Routes.ShipmentsReports,
        requiredPermissions: [Permission.ShipmentWatchListReports],
      },
    ],
  },
  [Routes.Plans]: {
    title: 'Plans & Pricing',
    path: Routes.Plans,
    requiredPermissions: [Permission.PlanAndPricing],
    isAlwaysAccessible: true,
  },
  [Routes.InvoiceHistory]: {
    title: 'Billing & Invoices',
    path: Routes.InvoiceHistory,
    isCompanyAdmin: true,
    isAlwaysAccessible: true,
  },
  [Routes.Settings]: {
    title: 'Settings',
    path: Routes.Settings,
    isAlwaysAccessible: true,
  },
  [Routes.JoinRequestValidation]: {
    title: 'Company Requests',
    path: Routes.JoinRequestValidation,
  },
  [Routes.ComponentPreview]: {
    title: 'Component Preview',
    path: Routes.ComponentPreview,
  },
  [Routes.JoinCompany]: {
    title: 'Join Company',
    path: Routes.JoinCompany,
  },
  [Routes.CreateCompany]: {
    title: 'Create Company',
    path: Routes.CreateCompany,
  },
  [Routes.AwaitingAdmin]: {
    title: 'Your Request Has Been Sent',
    path: Routes.AwaitingAdmin,
  },
};

export function matchPage(path: string, pages: Page[]): Page | undefined {
  // We should run this match against the actual routes
  let matches = matchRoutes(pages, path);
  let page: Page | undefined;

  while (matches !== null) {
    const match = matches[0];
    page = match.route as Page;

    if (page) {
      matches = matchRoutes(page.children || [], path);
    }
  }

  return page;
}

export function getPage(path: string) {
  const pages = Object.values(pageInformation);
  return matchPage(path, pages);
}

export function usePage() {
  const { pathname } = useLocation();
  const page = getPage(pathname);
  return page;
}

export function isPageSupported(page: Page, region: Region): boolean {
  const regionFeature = page.details?.regionFeature;
  return !regionFeature || !!region.features?.includes(regionFeature);
}

export function isPagePublic(path: string): boolean {
  const page = getPage(path);
  return !!page?.isPublic;
}
